/**
 * CollectiblesHUD - Visual HUD panel for collectible letters and numbers.
 *
 * Displays a compact grid of letters (A-Z) in 6 columns × 5 rows and
 * numbers (1-9) in a single row below, fixed to the camera in the
 * top-right corner. Supports expand/collapse toggle, collection animations,
 * and power-up celebration effects.
 *
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5, 11.6
 */

import { LAYER_DEPTHS } from '../scenes/GameScene';
import type { ICollectibleSystem } from './CollectibleSystem';

// ─── Interface ────────────────────────────────────────────────────────────────

export interface ICollectiblesHUD {
  create(scene: Phaser.Scene): void;
  update(collectibleSystem: ICollectibleSystem): void;
  toggle(): void;
  destroy(): void;
  animateCollection(id: string): void;
  showPowerUpCelebration(type: 'vowels' | 'numbers' | 'alphabet'): void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CELL_SIZE = 24;
const CELL_PADDING = 2;
const GRID_COLUMNS = 6;
const GRID_ROWS = 5; // 26 letters across 5 rows (last row has 2 items)
const NUMBER_COUNT = 9;
const PANEL_MARGIN = 8;
const TOGGLE_BUTTON_SIZE = 20;

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

const COLLECTED_COLOR = 0xffffff;
const UNCOLLECTED_COLOR = 0x888888;
const UNCOLLECTED_ALPHA = 0.3;
const COLLECTED_ALPHA = 1.0;
const GLOW_COLOR = 0xffdd44;

const PANEL_BG_COLOR = 0x1a2233;
const PANEL_BG_ALPHA = 0.6;
const BADGE_BG_COLOR = 0x1a2233;
const BADGE_BG_ALPHA = 0.6;

// ─── Implementation ───────────────────────────────────────────────────────────

export class CollectiblesHUD implements ICollectiblesHUD {
  private scene: Phaser.Scene | null = null;
  private container: Phaser.GameObjects.Container | null = null;
  private expanded: boolean = true;

  // Expanded panel elements
  private panelBg: Phaser.GameObjects.Rectangle | null = null;
  private letterCells: Map<string, Phaser.GameObjects.Text> = new Map();
  private numberCells: Map<string, Phaser.GameObjects.Text> = new Map();
  private letterGlows: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private numberGlows: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private toggleButton: Phaser.GameObjects.Text | null = null;

  // Collapsed badge elements
  private badgeContainer: Phaser.GameObjects.Container | null = null;
  private badgeBg: Phaser.GameObjects.Rectangle | null = null;
  private badgeText: Phaser.GameObjects.Text | null = null;

  // ─── Public Methods ───────────────────────────────────────────────────────

  /**
   * Create the HUD panel and attach it to the given scene.
   */
  create(scene: Phaser.Scene): void {
    this.scene = scene;

    // Main container — fixed to camera, top-right corner
    this.container = scene.add.container(0, 0);
    this.container.setDepth(LAYER_DEPTHS.HUD);
    this.container.setScrollFactor(0);

    this.createExpandedPanel();
    this.createCollapsedBadge();
    this.createToggleButton();

    // Position the container in the top-right corner
    this.updatePosition();

    // Start collapsed to avoid obstructing gameplay
    this.setExpanded(false);
  }

  /**
   * Update the HUD to reflect the current collectible system state.
   */
  update(collectibleSystem: ICollectibleSystem): void {
    if (!this.scene) return;

    // Re-position for dynamic viewport (EXPAND mode)
    this.updatePosition();

    // Update letter cells
    for (const [letter, collected] of collectibleSystem.letters) {
      const cell = this.letterCells.get(letter);
      const glow = this.letterGlows.get(letter);
      if (cell) {
        cell.setAlpha(collected ? COLLECTED_ALPHA : UNCOLLECTED_ALPHA);
        cell.setColor(collected ? '#ffffff' : '#888888');
      }
      if (glow) {
        glow.setVisible(collected);
      }
    }

    // Update number cells
    for (const [num, collected] of collectibleSystem.numbers) {
      const cell = this.numberCells.get(num);
      const glow = this.numberGlows.get(num);
      if (cell) {
        cell.setAlpha(collected ? COLLECTED_ALPHA : UNCOLLECTED_ALPHA);
        cell.setColor(collected ? '#ffffff' : '#888888');
      }
      if (glow) {
        glow.setVisible(collected);
      }
    }

    // Update badge text
    this.updateBadgeText(collectibleSystem);
  }

  /**
   * Toggle between expanded and collapsed state.
   */
  toggle(): void {
    this.setExpanded(!this.expanded);
  }

  /**
   * Destroy all HUD elements and clean up.
   */
  destroy(): void {
    if (this.container) {
      this.container.destroy(true);
      this.container = null;
    }
    this.letterCells.clear();
    this.numberCells.clear();
    this.letterGlows.clear();
    this.numberGlows.clear();
    this.panelBg = null;
    this.toggleButton = null;
    this.badgeContainer = null;
    this.badgeBg = null;
    this.badgeText = null;
    this.scene = null;
  }

  /**
   * Animate a specific cell when the corresponding collectible is collected.
   * Plays a pulse/scale animation on the cell.
   */
  animateCollection(id: string): void {
    if (!this.scene) return;

    const cell = this.getCellById(id);
    if (!cell) return;

    // Pulse/scale animation
    this.scene.tweens.add({
      targets: cell,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 150,
      yoyo: true,
      ease: 'Back.easeOut',
      onComplete: () => {
        cell.setScale(1);
      },
    });
  }

  /**
   * Show a celebration effect on the relevant row/section when a power-up is triggered.
   */
  showPowerUpCelebration(type: 'vowels' | 'numbers' | 'alphabet'): void {
    if (!this.scene) return;

    const cells = this.getCelebrationCells(type);

    // Flash/glow effect on each relevant cell
    cells.forEach((cell, index) => {
      this.scene!.tweens.add({
        targets: cell,
        scaleX: 1.4,
        scaleY: 1.4,
        duration: 200,
        delay: index * 50,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          cell.setScale(1);
        },
      });
    });
  }

  // ─── Private Methods ──────────────────────────────────────────────────────

  /**
   * Create the expanded panel with letter grid and number row.
   */
  private createExpandedPanel(): void {
    if (!this.scene || !this.container) return;

    // Calculate panel dimensions
    const gridWidth = GRID_COLUMNS * (CELL_SIZE + CELL_PADDING) - CELL_PADDING;
    const gridHeight = GRID_ROWS * (CELL_SIZE + CELL_PADDING) - CELL_PADDING;
    const numberRowWidth = NUMBER_COUNT * (CELL_SIZE + CELL_PADDING) - CELL_PADDING;
    const panelWidth = Math.max(gridWidth, numberRowWidth) + PANEL_MARGIN * 2;
    const numberRowY = gridHeight + CELL_PADDING * 2;
    const panelHeight = numberRowY + CELL_SIZE + PANEL_MARGIN * 2;

    // Panel background
    this.panelBg = this.scene.add.rectangle(
      panelWidth / 2,
      panelHeight / 2 + TOGGLE_BUTTON_SIZE,
      panelWidth,
      panelHeight,
      PANEL_BG_COLOR,
      PANEL_BG_ALPHA
    );
    this.panelBg.setOrigin(0.5);
    this.container.add(this.panelBg);

    // Create letter cells (A-Z) in a 6×5 grid
    const gridOffsetX = PANEL_MARGIN;
    const gridOffsetY = PANEL_MARGIN + TOGGLE_BUTTON_SIZE;

    for (let i = 0; i < ALPHABET.length; i++) {
      const letter = ALPHABET[i];
      const col = i % GRID_COLUMNS;
      const row = Math.floor(i / GRID_COLUMNS);
      const x = gridOffsetX + col * (CELL_SIZE + CELL_PADDING) + CELL_SIZE / 2;
      const y = gridOffsetY + row * (CELL_SIZE + CELL_PADDING) + CELL_SIZE / 2;

      // Glow background for collected state
      const glow = this.scene.add.rectangle(x, y, CELL_SIZE, CELL_SIZE, GLOW_COLOR, 0.3);
      glow.setVisible(false);
      this.container.add(glow);
      this.letterGlows.set(letter, glow);

      // Letter text
      const text = this.scene.add.text(x, y, letter, {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#888888',
        fontStyle: 'bold',
        align: 'center',
      });
      text.setOrigin(0.5);
      text.setAlpha(UNCOLLECTED_ALPHA);
      this.container.add(text);
      this.letterCells.set(letter, text);
    }

    // Create number cells (1-9) in a single row below the letter grid
    const numberOffsetY = gridOffsetY + gridHeight + CELL_PADDING * 2 + CELL_SIZE / 2;

    for (let i = 0; i < NUMBERS.length; i++) {
      const num = NUMBERS[i];
      const x = gridOffsetX + i * (CELL_SIZE + CELL_PADDING) + CELL_SIZE / 2;
      const y = numberOffsetY;

      // Glow background for collected state
      const glow = this.scene.add.rectangle(x, y, CELL_SIZE, CELL_SIZE, GLOW_COLOR, 0.3);
      glow.setVisible(false);
      this.container.add(glow);
      this.numberGlows.set(num, glow);

      // Number text
      const text = this.scene.add.text(x, y, num, {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#888888',
        fontStyle: 'bold',
        align: 'center',
      });
      text.setOrigin(0.5);
      text.setAlpha(UNCOLLECTED_ALPHA);
      this.container.add(text);
      this.numberCells.set(num, text);
    }
  }

  /**
   * Create the collapsed badge (small count indicator).
   */
  private createCollapsedBadge(): void {
    if (!this.scene || !this.container) return;

    this.badgeContainer = this.scene.add.container(0, TOGGLE_BUTTON_SIZE + PANEL_MARGIN);

    // Badge background
    this.badgeBg = this.scene.add.rectangle(
      50, 14, 100, 28, BADGE_BG_COLOR, BADGE_BG_ALPHA
    );
    this.badgeBg.setOrigin(0.5);
    this.badgeContainer.add(this.badgeBg);

    // Badge text
    this.badgeText = this.scene.add.text(50, 14, '0/35 ✓', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold',
      align: 'center',
    });
    this.badgeText.setOrigin(0.5);
    this.badgeContainer.add(this.badgeText);

    this.badgeContainer.setVisible(false);
    this.container.add(this.badgeContainer);
  }

  /**
   * Create the toggle button for expand/collapse.
   */
  private createToggleButton(): void {
    if (!this.scene || !this.container) return;

    this.toggleButton = this.scene.add.text(0, 0, '▼', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      backgroundColor: '#333333',
      padding: { x: 4, y: 2 },
    });
    this.toggleButton.setOrigin(0, 0);
    this.toggleButton.setInteractive({ useHandCursor: true });
    this.toggleButton.on('pointerdown', () => {
      this.toggle();
    });

    this.container.add(this.toggleButton);
  }

  /**
   * Set expanded/collapsed state and update visibility.
   */
  private setExpanded(expanded: boolean): void {
    this.expanded = expanded;

    // Toggle panel elements visibility
    if (this.panelBg) this.panelBg.setVisible(expanded);

    for (const cell of this.letterCells.values()) {
      cell.setVisible(expanded);
    }
    for (const cell of this.numberCells.values()) {
      cell.setVisible(expanded);
    }
    for (const glow of this.letterGlows.values()) {
      // Glows are only visible when expanded AND collected
      if (!expanded) glow.setVisible(false);
    }
    for (const glow of this.numberGlows.values()) {
      if (!expanded) glow.setVisible(false);
    }

    // Toggle badge visibility (opposite of panel)
    if (this.badgeContainer) {
      this.badgeContainer.setVisible(!expanded);
    }

    // Update toggle button text
    if (this.toggleButton) {
      this.toggleButton.setText(expanded ? '▼' : '▶');
    }
  }

  /**
   * Position the container in the top-right corner of the viewport.
   */
  private updatePosition(): void {
    if (!this.scene || !this.container) return;

    // Use camera width to account for EXPAND mode on wider screens
    const width = this.scene.cameras.main.width;
    const gridWidth = GRID_COLUMNS * (CELL_SIZE + CELL_PADDING) - CELL_PADDING;
    const panelWidth = gridWidth + PANEL_MARGIN * 2;

    // Position at top-right with some margin from edge
    const x = width - panelWidth - PANEL_MARGIN;
    const y = PANEL_MARGIN;

    this.container.setPosition(x, y);
  }

  /**
   * Update the badge text with the current collected count.
   */
  private updateBadgeText(collectibleSystem: ICollectibleSystem): void {
    if (!this.badgeText) return;

    let letterCount = 0;
    for (const collected of collectibleSystem.letters.values()) {
      if (collected) letterCount++;
    }

    let numberCount = 0;
    for (const collected of collectibleSystem.numbers.values()) {
      if (collected) numberCount++;
    }

    const total = letterCount + numberCount;
    const max = 26 + 9; // 35 total collectibles
    this.badgeText.setText(`${total}/${max} ✓`);
  }

  /**
   * Get a text cell by collectible ID (e.g. "letter-A", "number-3").
   */
  private getCellById(id: string): Phaser.GameObjects.Text | undefined {
    if (id.startsWith('letter-')) {
      const value = id.slice(7);
      return this.letterCells.get(value);
    } else if (id.startsWith('number-')) {
      const value = id.slice(7);
      return this.numberCells.get(value);
    }
    return undefined;
  }

  /**
   * Get the cells relevant for a power-up celebration by type.
   */
  private getCelebrationCells(type: 'vowels' | 'numbers' | 'alphabet'): Phaser.GameObjects.Text[] {
    const cells: Phaser.GameObjects.Text[] = [];

    switch (type) {
      case 'vowels':
        for (const vowel of ['A', 'E', 'I', 'O', 'U']) {
          const cell = this.letterCells.get(vowel);
          if (cell) cells.push(cell);
        }
        break;
      case 'numbers':
        for (const num of NUMBERS) {
          const cell = this.numberCells.get(num);
          if (cell) cells.push(cell);
        }
        break;
      case 'alphabet':
        for (const letter of ALPHABET) {
          const cell = this.letterCells.get(letter);
          if (cell) cells.push(cell);
        }
        break;
    }

    return cells;
  }
}
